import React from 'react'
import { BaseWidget } from '../BaseWidget'
import { WidgetTitle } from '../Atoms'
import { Flex, Grid, Typography } from 'components'
import { DeltaBadge, DeltaBadgeSkeleton } from '../Atoms/DeltaBadge'
import { WidgetProps } from '../types'
import { LineGraph } from '../Atoms/LineGraph'
import { TypographySkeleton } from 'components/Skeletons/TypographySkeleton'
import { getDateFilterVersusText, getShortNumber } from '../utils'

type InfoData = {
  info: number
  unit?: string
  previousPeriod?: number
  historicInfo?: Array<{
    date: string
    info: number
  }>
}

export type InfoWidgetProps = WidgetProps<InfoData> & { showZero?: boolean; fetching?: boolean }

export const InfoWidget: React.FC<InfoWidgetProps> = (props) => {
  return (
    <BaseWidget size={props.size} debug={props.debug}>
      <Content {...props} />
    </BaseWidget>
  )
}

const Content: React.FC<InfoWidgetProps> = (props) => {
  if (props.size === '2x2') {
    return <LGContent {...props} />
  } else if (props.size === '1x2') {
    return <MDContent {...props} />
  }
  return <SMContent {...props} />
}

const SMContent: React.FC<InfoWidgetProps> = (props) => (
  <Flex directionColumn="zero" between="zero" fullHeight>
    <InfoWidgetTitle {...props} />
    <Flex directionColumn="zero" gap={0.5}>
      <InfoWidgetInfo {...props} />
      <InfoWidgetDeltaBadge {...props} />
    </Flex>
  </Flex>
)

const MDContent: React.FC<InfoWidgetProps> = (props) => (
  <Grid gap={1} columns={2} fullHeight>
    <Flex directionColumn="zero" between="zero" fullHeight>
      <InfoWidgetTitle {...props} />
      <Flex directionColumn="zero" gap={0.5}>
        <InfoWidgetInfo {...props} />
      </Flex>
    </Flex>
    <Flex directionColumn="zero" between="zero" fullHeight>
      <LineGraph
        showZero={props.showZero}
        color={getMood(props.data?.info ?? 0, props.data?.previousPeriod ?? 0)}
        data={props.data?.historicInfo?.map(({ date, info }) => ({
          date,
          info,
        }))}
        loading={props.loading}
        fetching={props.fetching}
        unit={props.data?.unit}
        size={props.size}
      />
      <InfoWidgetDeltaBadge {...props} />
    </Flex>
  </Grid>
)

const LGContent: React.FC<InfoWidgetProps> = (props) => (
  <Grid rows={2} gap={1} fullHeight>
    <Flex directionColumn="zero" between="zero" fullHeight>
      <InfoWidgetTitle {...props} />
      <Flex gap={0.5} between="zero">
        <InfoWidgetInfo {...props} />
        <InfoWidgetDeltaBadge {...props} />
      </Flex>
    </Flex>
    <LineGraph
      color={getMood(props.data?.info ?? 0, props.data?.previousPeriod ?? 0)}
      data={props.data?.historicInfo?.map(({ date, info }) => ({
        date,
        info,
      }))}
      loading={props.loading}
      fetching={props.fetching}
      size={props.size}
      unit={props.data?.unit}
      showZero={props.showZero}
    />
  </Grid>
)

const InfoWidgetTitle: React.FC<InfoWidgetProps> = (props) => (
  <WidgetTitle>
    {props.title} {props.data?.unit ? `(${props.data.unit})` : ''}
  </WidgetTitle>
)

const InfoWidgetInfo: React.FC<InfoWidgetProps> = ({ data, loading }) => {
  if (loading) {
    return <TypographySkeleton height="header_lg" width="6rem" />
  }
  return (
    <Typography fontSize="header_lg" fontWeight="semibold" color="grey900">
      {data ? getShortNumber(data.info) : '-'}
    </Typography>
  )
}

const InfoWidgetDeltaBadge: React.FC<InfoWidgetProps> = (props) => {
  if (props.loading) {
    return <DeltaBadgeSkeleton />
  }
  const previousPeriod = props.data?.previousPeriod
  if (!previousPeriod) {
    return (
      <DeltaBadge
        trend="stable"
        value="-%"
        variant={'stable'}
        text={getDateFilterVersusText(props.currentFilter)}
      />
    )
  }

  const difference = (props.data?.info ?? 0) - previousPeriod
  const percentage = previousPeriod !== 0 ? Math.abs((difference / previousPeriod) * 100) : 0

  return (
    <DeltaBadge
      trend={difference === 0 ? 'stable' : difference > 0 ? 'up' : 'down'}
      value={percentage.toFixed(0) + '%'}
      variant={difference === 0 ? 'stable' : difference > 0 ? 'success' : 'warning'}
      text={`${getDateFilterVersusText(props.currentFilter)} (${getShortNumber(previousPeriod)})`}
    />
  )
}

const getMood = (data: number, prev: number) => {
  const difference = data - prev
  if (difference > 0) {
    return 'green'
  } else if (difference < 0) {
    return 'orange'
  } else {
    return 'grey'
  }
}
